import React, { useEffect, useState } from "react";
import { api, formatDateTime, formatApiError } from "../lib/api";
import { PageHeader, StatusBadge, EmptyState, LoadingRows } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Plus } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Approvals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "asset_procurement", details: "" });
  const load = async () => { const { data } = await api.get("/approvals", { params: { limit: 100 } }); setItems(data.items); setLoading(false); };
  useEffect(() => { load(); }, []);
  const decide = async (a, decision) => {
    try { await api.post(`/approvals/${a.id}/decision`, null, { params: { decision, comments: "" } }); toast.success(`Request ${decision}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submit = async () => {
    try { await api.post("/approvals", form); toast.success("Approval request submitted"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (loading) return <LoadingRows rows={6} />;
  return (
    <>
      <PageHeader title="Approvals" description="Enforce sign-off for critical actions across procurement, capital, pricing and asset lifecycle."
        actions={<Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="approval-new-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> New request</Button>} />
      <Card className="rounded-xl divide-y">
        {items.length === 0 ? <div className="p-6"><EmptyState title="No approvals yet" /></div> :
          items.map((a) => (
            <div key={a.id} className="p-4 flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><div className="font-medium">{a.title || a.type}</div><StatusBadge status={a.status} /></div>
                <div className="text-sm text-muted-foreground mt-1">{a.details}</div>
                <div className="text-xs text-muted-foreground mt-1">Requested by {a.requester_email} · {formatDateTime(a.created_at)}</div>
                {a.comments && <div className="text-xs mt-1"><span className="text-muted-foreground">Comment:</span> {a.comments}</div>}
              </div>
              {a.status === "pending" && user.role === "super_admin" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => decide(a, "rejected")} data-testid={`approval-reject-${a.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(a, "approved")} data-testid={`approval-approve-${a.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
                </div>
              )}
            </div>
          ))}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New approval request</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1"><Label>Type</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="asset_procurement | capital_allocation | pricing_change | asset_writeoff" /></div>
            <div className="space-y-1"><Label>Details</Label><Input value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={submit} data-testid="approval-submit-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
